import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PageHeader } from '../components/ui';
import { routeById } from '../navigation/routes';

interface MemeTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  popularityScore?: number;
  imageUrl?: string;
  imgflipId?: string;
  width?: number;
  height?: number;
  boxCount?: number;
}

interface MemeTextResult {
  topText: string;
  bottomText: string;
  texts?: string[];
  template: MemeTemplate;
  explanation: string;
  generatedImageUrl?: string;
}

// Defines where text boxes should be placed on the meme image (all values as % of image dimensions)
interface TextBoxRegion {
  centerX: number;
  centerY: number;
  maxWidth: number;
  maxHeight: number;
}

// User-adjustable text position (percentage of image dimensions)
interface TextPosition {
  x: number; // centerX as % of image width
  y: number; // centerY as % of image height
  fontSize: number; // font size in px (relative to rendered preview)
  maxWidth: number; // max text box width as % of image width (keeps wrapping consistent)
}

// Known meme template text positions by Imgflip template ID
const MEME_TEXT_REGIONS: Record<string, TextBoxRegion[]> = {
  // Drake Hotline Bling - text on right side of each panel
  '181913649': [
    { centerX: 75, centerY: 25, maxWidth: 45, maxHeight: 45 },
    { centerX: 75, centerY: 75, maxWidth: 45, maxHeight: 45 },
  ],
  // Distracted Boyfriend - labels on each of the three people
  '112126428': [
    { centerX: 80, centerY: 42, maxWidth: 24, maxHeight: 35 }, // the other woman (right)
    { centerX: 53, centerY: 70, maxWidth: 22, maxHeight: 30 }, // the boyfriend (center)
    { centerX: 20, centerY: 55, maxWidth: 22, maxHeight: 35 }, // the girlfriend (left)
  ],
  // Two Buttons - text on each button area
  '87743020': [
    { centerX: 33, centerY: 12, maxWidth: 28, maxHeight: 20 },
    { centerX: 67, centerY: 18, maxWidth: 28, maxHeight: 20 },
  ],
  // Expanding Brain - text on the left of each of the four panels
  '93895088': [
    { centerX: 25, centerY: 12, maxWidth: 46, maxHeight: 20 },
    { centerX: 25, centerY: 37, maxWidth: 46, maxHeight: 20 },
    { centerX: 25, centerY: 62, maxWidth: 46, maxHeight: 20 },
    { centerX: 25, centerY: 87, maxWidth: 46, maxHeight: 20 },
  ],
  // Change My Mind - text on the sign
  '129242436': [
    { centerX: 50, centerY: 62, maxWidth: 55, maxHeight: 28 },
    { centerX: 50, centerY: 10, maxWidth: 80, maxHeight: 15 },
  ],
  // Is This A Pigeon - butterfly label + bottom question
  '100777631': [
    { centerX: 62, centerY: 12, maxWidth: 30, maxHeight: 15 },
    { centerX: 50, centerY: 90, maxWidth: 80, maxHeight: 15 },
  ],
  // Woman Yelling at Cat - text in each half
  '188390779': [
    { centerX: 30, centerY: 88, maxWidth: 48, maxHeight: 18 },
    { centerX: 75, centerY: 88, maxWidth: 45, maxHeight: 18 },
  ],
  // Always Has Been - astronaut speech bubbles
  '252600902': [
    { centerX: 22, centerY: 82, maxWidth: 38, maxHeight: 18 },
    { centerX: 70, centerY: 35, maxWidth: 35, maxHeight: 20 },
  ],
  // Left Exit 12 Off Ramp - text on highway signs
  '124822590': [
    { centerX: 62, centerY: 12, maxWidth: 28, maxHeight: 20 },
    { centerX: 30, centerY: 15, maxWidth: 28, maxHeight: 20 },
  ],
  // Epic Handshake - labels on each arm
  '135256802': [
    { centerX: 18, centerY: 40, maxWidth: 30, maxHeight: 30 },
    { centerX: 82, centerY: 40, maxWidth: 30, maxHeight: 30 },
  ],
  // Boardroom Meeting Suggestion - text on suggestion signs
  '1338610': [
    { centerX: 50, centerY: 10, maxWidth: 80, maxHeight: 15 },
    { centerX: 50, centerY: 80, maxWidth: 40, maxHeight: 15 },
  ],
  // This Is Fine - text on/near dog
  '55311130': [
    { centerX: 50, centerY: 10, maxWidth: 80, maxHeight: 15 },
    { centerX: 50, centerY: 75, maxWidth: 50, maxHeight: 20 },
  ],
  // Surprised Pikachu
  '155067746': [
    { centerX: 50, centerY: 10, maxWidth: 80, maxHeight: 15 },
    { centerX: 50, centerY: 88, maxWidth: 80, maxHeight: 15 },
  ],
  // One Does Not Simply - classic top/bottom caption
  '61579': [
    { centerX: 50, centerY: 8, maxWidth: 90, maxHeight: 15 },
    { centerX: 50, centerY: 90, maxWidth: 90, maxHeight: 15 },
  ],
  // Batman Slapping Robin - speech in each panel
  '438680': [
    { centerX: 28, centerY: 18, maxWidth: 38, maxHeight: 25 },
    { centerX: 72, centerY: 18, maxWidth: 38, maxHeight: 25 },
  ],
  // Waiting Skeleton - top setup / bottom punchline
  '4087833': [
    { centerX: 50, centerY: 10, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 88, maxWidth: 85, maxHeight: 15 },
  ],
  // Disaster Girl
  '97984': [
    { centerX: 50, centerY: 8, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 90, maxWidth: 85, maxHeight: 15 },
  ],
  // Mocking Spongebob - statement on top, mocking text on bottom
  '102156234': [
    { centerX: 50, centerY: 8, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 90, maxWidth: 85, maxHeight: 15 },
  ],
  // Running Away Balloon - labels on person and balloon
  '131087935': [
    { centerX: 30, centerY: 78, maxWidth: 30, maxHeight: 18 },
    { centerX: 72, centerY: 30, maxWidth: 30, maxHeight: 18 },
  ],
  // Hide the Pain Harold - top/bottom caption
  '27813981': [
    { centerX: 50, centerY: 10, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 88, maxWidth: 85, maxHeight: 15 },
  ],
  // Sad Pablo Escobar
  '80707627': [
    { centerX: 50, centerY: 8, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 90, maxWidth: 85, maxHeight: 15 },
  ],
  // Tuxedo Winnie the Pooh - plain text / fancy text per panel
  '178591752': [
    { centerX: 73, centerY: 25, maxWidth: 48, maxHeight: 40 },
    { centerX: 73, centerY: 75, maxWidth: 48, maxHeight: 40 },
  ],
  // X, X Everywhere (Buzz & Woody)
  '91538330': [
    { centerX: 50, centerY: 8, maxWidth: 70, maxHeight: 14 },
    { centerX: 50, centerY: 88, maxWidth: 90, maxHeight: 14 },
  ],
  // Ancient Aliens guy
  '101470': [
    { centerX: 50, centerY: 8, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 90, maxWidth: 85, maxHeight: 15 },
  ],
  // Bernie Sanders 'I am once again asking'
  '222403160': [
    { centerX: 50, centerY: 8, maxWidth: 85, maxHeight: 15 },
    { centerX: 50, centerY: 88, maxWidth: 85, maxHeight: 18 },
  ],
  // Gru's Plan - four panels
  '131940431': [
    { centerX: 27, centerY: 22, maxWidth: 38, maxHeight: 22 },
    { centerX: 73, centerY: 22, maxWidth: 38, maxHeight: 22 },
    { centerX: 27, centerY: 72, maxWidth: 38, maxHeight: 22 },
    { centerX: 73, centerY: 72, maxWidth: 38, maxHeight: 22 },
  ],
};

const DEFAULT_FONT_SIZE = 24;
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 72;
const FONT_SIZE_STEP = 2;
const DEFAULT_MAX_WIDTH = 80; // % of image width

// Selectable fonts (each value includes web-safe fallbacks ending in Impact/sans-serif)
const FONT_OPTIONS: { label: string; value: string }[] = [
  { label: 'Impact (Klassisch)', value: '"Impact", "Anton", "Arial Black", sans-serif' },
  { label: 'Arial Black', value: '"Arial Black", "Arial Bold", Gadget, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, Tahoma, sans-serif' },
  { label: 'Comic Sans', value: '"Comic Sans MS", "Comic Sans", cursive' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
];
const DEFAULT_FONT_FAMILY = FONT_OPTIONS[0].value;
const DEFAULT_TEXT_COLOR = '#FFFFFF';
const DEFAULT_OUTLINE_COLOR = '#000000';

// Build text box positions for a template with `count` boxes. Uses the known
// region map when available and fills any remaining boxes with sensible,
// evenly distributed defaults so templates with >2 boxes are placed correctly.
const buildPositionsForTemplate = (
  imgflipId: string | undefined,
  count: number
): TextPosition[] => {
  const safeCount = Math.max(1, count);
  const regions = imgflipId ? MEME_TEXT_REGIONS[imgflipId] : undefined;

  return Array.from({ length: safeCount }, (_, i) => {
    const region = regions?.[i];
    if (region) {
      return { x: region.centerX, y: region.centerY, fontSize: DEFAULT_FONT_SIZE, maxWidth: region.maxWidth };
    }
    // Evenly distribute boxes vertically between 10% and 90%.
    const y = safeCount === 1 ? 12 : 10 + (80 * i) / (safeCount - 1);
    return { x: 50, y, fontSize: DEFAULT_FONT_SIZE, maxWidth: DEFAULT_MAX_WIDTH };
  });
};

// Build the classic 8-direction text outline used in the live preview overlay.
const buildTextOutlineShadow = (color: string, width = 2): string =>
  [
    `${width}px ${width}px 0 ${color}`,
    `-${width}px -${width}px 0 ${color}`,
    `${width}px -${width}px 0 ${color}`,
    `-${width}px ${width}px 0 ${color}`,
    `0 ${width}px 0 ${color}`,
    `0 -${width}px 0 ${color}`,
    `${width}px 0 0 ${color}`,
    `-${width}px 0 0 ${color}`,
  ].join(', ');

const MemeGeneratorPage: React.FC = () => {
  const [templates, setTemplates] = useState<MemeTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [topic, setTopic] = useState('');
  const [targetAudience, setTargetAudience] = useState('professionals and business leaders');
  const [tone, setTone] = useState<'professional' | 'casual' | 'humorous' | 'inspirational'>('professional');
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [result, setResult] = useState<MemeTextResult | null>(null);
  const [error, setError] = useState<string>('');
  const [texts, setTexts] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  // Draggable text state
  const [textPositions, setTextPositions] = useState<TextPosition[]>([
    { x: 50, y: 10, fontSize: DEFAULT_FONT_SIZE, maxWidth: DEFAULT_MAX_WIDTH },
    { x: 50, y: 90, fontSize: DEFAULT_FONT_SIZE, maxWidth: DEFAULT_MAX_WIDTH },
  ]);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  // Text styling (applies to both text boxes)
  const [fontFamily, setFontFamily] = useState<string>(DEFAULT_FONT_FAMILY);
  const [textColor, setTextColor] = useState<string>(DEFAULT_TEXT_COLOR);
  const [outlineColor, setOutlineColor] = useState<string>(DEFAULT_OUTLINE_COLOR);
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/memes/templates`);
      const data = await response.json();

      if (data.success) {
        setTemplates(data.templates);
      } else {
        setError(data.error || 'Failed to load meme templates');
      }
    } catch (err: any) {
      setError(`Error loading templates: ${err.message}`);
    } finally {
      setLoadingTemplates(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Initialize text positions from template regions for a given number of boxes
  const initializeTextPositions = useCallback((template: MemeTemplate, count: number) => {
    setTextPositions(buildPositionsForTemplate(template.imgflipId, count));
  }, []);

  // Get mouse/touch position relative to preview container as percentage
  const getRelativePosition = useCallback((clientX: number, clientY: number) => {
    const container = previewContainerRef.current;
    if (!container) return { x: 0, y: 0 };
    const rect = container.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  }, []);

  // Drag handlers
  const handleDragStart = useCallback((index: number, clientX: number, clientY: number) => {
    setDraggingIndex(index);
    const pos = getRelativePosition(clientX, clientY);
    dragOffsetRef.current = {
      x: pos.x - textPositions[index].x,
      y: pos.y - textPositions[index].y,
    };
  }, [textPositions, getRelativePosition]);

  const handleDragMove = useCallback((clientX: number, clientY: number) => {
    if (draggingIndex === null) return;
    const pos = getRelativePosition(clientX, clientY);
    setTextPositions((prev: TextPosition[]) => {
      const updated = [...prev];
      updated[draggingIndex] = {
        ...updated[draggingIndex],
        x: Math.max(0, Math.min(100, pos.x - dragOffsetRef.current.x)),
        y: Math.max(0, Math.min(100, pos.y - dragOffsetRef.current.y)),
      };
      return updated;
    });
  }, [draggingIndex, getRelativePosition]);

  const handleDragEnd = useCallback(() => {
    setDraggingIndex(null);
  }, []);

  // Mouse event handlers for the preview container
  const onMouseDown = useCallback((index: number, e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(index, e.clientX, e.clientY);
  }, [handleDragStart]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    handleDragMove(e.clientX, e.clientY);
  }, [handleDragMove]);

  const onMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Touch event handlers
  const onTouchStart = useCallback((index: number, e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      handleDragStart(index, touch.clientX, touch.clientY);
    }
  }, [handleDragStart]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && draggingIndex !== null) {
      e.preventDefault();
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    }
  }, [handleDragMove, draggingIndex]);

  const onTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Font size handlers
  const adjustFontSize = useCallback((index: number, delta: number) => {
    setTextPositions((prev: TextPosition[]) => {
      const updated = [...prev];
      const newSize = Math.max(MIN_FONT_SIZE, Math.min(MAX_FONT_SIZE, updated[index].fontSize + delta));
      updated[index] = { ...updated[index], fontSize: newSize };
      return updated;
    });
  }, []);

  /**
   * Render meme image on a canvas using user-adjusted positions.
   * Returns a data URL of the generated image.
   */
  const renderMemeForDownload = useCallback((
    templateImageUrl: string,
    boxTexts: string[],
    positions: TextPosition[],
    style: { fontFamily: string; textColor: string; outlineColor: string }
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
          reject(new Error('Canvas not available'));
          return;
        }

        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        // Draw template image
        ctx.drawImage(img, 0, 0);

        const drawTextAtPosition = (text: string, position: TextPosition) => {
          const centerXPx = (position.x / 100) * img.width;
          const centerYPx = (position.y / 100) * img.height;

          // Scale font size from preview to actual image size
          // The preview is displayed at a certain width; the actual image may be larger
          const previewContainer = previewContainerRef.current;
          const scale = previewContainer ? img.width / previewContainer.clientWidth : 1;
          const fontSize = Math.max(14, Math.round(position.fontSize * scale));

          ctx.font = `bold ${fontSize}px ${style.fontFamily}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'alphabetic';
          ctx.lineJoin = 'round';
          ctx.miterLimit = 2;

          // Use the same maxWidth (as % of image width) that constrains the live
          // preview overlay, so the downloaded image wraps text identically.
          // Then clamp so a text box near an edge can't extend past the image.
          const desiredWidth = img.width * (position.maxWidth / 100);
          const edgeLimit = 2 * Math.min(centerXPx, img.width - centerXPx);
          const maxWidth = Math.max(
            img.width * 0.2,
            Math.min(desiredWidth, edgeLimit, img.width * 0.95)
          );
          const words = text.toUpperCase().split(' ');
          const lines: string[] = [];
          let currentLine = '';

          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth && currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
          if (currentLine) lines.push(currentLine);

          const lineHeight = fontSize * 1.2;
          const totalHeight = lines.length * lineHeight;
          // Center text block vertically on centerYPx, then clamp within image bounds
          let startY = centerYPx - totalHeight / 2 + fontSize;
          startY = Math.max(fontSize, Math.min(img.height - totalHeight + fontSize, startY));

          // Outline width proportional to font size (classic meme look ~ 1/8).
          const outlineWidth = Math.max(2, fontSize / 8);

          lines.forEach((line, i) => {
            const lineY = startY + i * lineHeight;
            ctx.strokeStyle = style.outlineColor;
            ctx.lineWidth = outlineWidth;
            ctx.strokeText(line, centerXPx, lineY, maxWidth);
            ctx.fillStyle = style.textColor;
            ctx.fillText(line, centerXPx, lineY, maxWidth);
          });
        };

        boxTexts.forEach((text, i) => {
          const position = positions[i];
          if (position && text.trim()) {
            drawTextAtPosition(text, position);
          }
        });

        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('Bild konnte nicht geladen werden'));
      img.src = templateImageUrl;
    });
  }, []);

  /**
   * Single-step: generate text via API, then show interactive preview.
   */
  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!topic.trim()) {
      setError('Bitte gib ein Thema ein');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/memes/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic,
          targetAudience,
          tone,
          templateId: selectedTemplate || undefined,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setError(data.error || 'Meme-Text konnte nicht generiert werden');
        return;
      }

      const textResult: MemeTextResult = data.result;
      setResult(textResult);

      // Use all text boxes returned by the API; fall back to top/bottom for
      // older responses. The template's box count drives how many fields show.
      const boxTexts = textResult.texts && textResult.texts.length > 0
        ? textResult.texts
        : [textResult.topText, textResult.bottomText];
      const boxCount = Math.max(
        boxTexts.length,
        textResult.template.boxCount && textResult.template.boxCount > 0 ? textResult.template.boxCount : 0
      );
      // Pad texts to match the number of boxes so every box is editable.
      const paddedTexts = Array.from({ length: boxCount }, (_, i) => boxTexts[i] ?? '');
      setTexts(paddedTexts);

      // Initialize text positions based on template regions / box count
      initializeTextPositions(textResult.template, boxCount);
    } catch (err: any) {
      setError(`Fehler: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;

    if (result.template.imageUrl) {
      try {
        const dataUrl = await renderMemeForDownload(
          result.template.imageUrl,
          texts,
          textPositions,
          { fontFamily, textColor, outlineColor }
        );
        const link = document.createElement('a');
        link.download = `meme-${result.template.name.replace(/\s+/g, '-').toLowerCase()}.png`;
        link.href = dataUrl;
        link.click();
      } catch {
        setError('Bild konnte nicht zum Download gerendert werden. Versuche einen Screenshot.');
      }
    }
  };

  const handleReset = () => {
    setResult(null);
    setTexts([]);
    setError('');
    setTextPositions([
      { x: 50, y: 10, fontSize: DEFAULT_FONT_SIZE, maxWidth: DEFAULT_MAX_WIDTH },
      { x: 50, y: 90, fontSize: DEFAULT_FONT_SIZE, maxWidth: DEFAULT_MAX_WIDTH },
    ]);
    setFontFamily(DEFAULT_FONT_FAMILY);
    setTextColor(DEFAULT_TEXT_COLOR);
    setOutlineColor(DEFAULT_OUTLINE_COLOR);
  };

  // Update a single text box's content.
  const updateText = useCallback((index: number, value: string) => {
    setTexts((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  }, []);

  // Common text overlay style
  const getTextOverlayStyle = (position: TextPosition, isDragging: boolean): React.CSSProperties => {
    // Calculate maxWidth based on position so text doesn't extend beyond container edges.
    // A div centered at position.x% can extend at most position.x% to the left
    // and (100 - position.x)% to the right, so max full width = 2 * min(x, 100-x).
    const maxLeftWidth = 2 * position.x;
    const maxRightWidth = 2 * (100 - position.x);
    // Honour the template's configured box width, but never exceed the container edges.
    const effectiveMaxWidth = Math.max(
      20,
      Math.min(position.maxWidth, maxLeftWidth, maxRightWidth)
    );

    return {
      position: 'absolute',
      left: `${position.x}%`,
      top: `${position.y}%`,
      transform: 'translate(-50%, -50%)',
      color: textColor,
      fontFamily,
      fontWeight: 'bold',
      fontSize: `${position.fontSize}px`,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      lineHeight: '1.2',
      textShadow: buildTextOutlineShadow(outlineColor, 2),
      overflowWrap: 'break-word',
      wordBreak: 'normal',
      textAlign: 'center',
      cursor: isDragging ? 'grabbing' : 'grab',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      maxWidth: `${effectiveMaxWidth}%`,
      padding: '4px 8px',
      borderRadius: '4px',
      border: isDragging ? '2px dashed rgba(255,255,255,0.8)' : '2px dashed transparent',
      transition: isDragging ? 'none' : 'border-color 0.2s',
      zIndex: isDragging ? 10 : 1,
      pointerEvents: 'auto' as const,
    };
  };

  return (
    <div className="page-container">
      <PageHeader title="Meme-Generator" subtitle={routeById('memes')?.description} />

      {/* Hidden canvas for image rendering */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="grid-2-col mb-6">
        {/* Left Column - Form */}
        <div className="card">
          <h2 className="card-title mb-4">Meme-Konfiguration</h2>

          <form onSubmit={handleGenerate}>
            <div className="form-group">
              <label className="form-label">Thema / Topic *</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="z.B. Remote Work, Projektmanagement, AI im Business..."
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Zielgruppe</label>
              <input
                type="text"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="z.B. IT-Professionals, Sales Teams, Startup Gründer..."
                className="form-input"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tonalität</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as any)}
                className="form-select"
                disabled={loading}
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="humorous">Humorous</option>
                <option value="inspirational">Inspirational</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label mb-4">Meme Template Auswahl</label>
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--space-3)'
              }}>
                <label className={`form-check ${selectedTemplate === '' ? 'font-bold' : ''}`}
                  style={{
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-sm)',
                    border: selectedTemplate === '' ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                    backgroundColor: selectedTemplate === '' ? 'var(--color-primary-50)' : 'var(--color-surface)'
                  }}
                >
                  <input
                    type="radio"
                    name="template"
                    value=""
                    checked={selectedTemplate === ''}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    disabled={loading || loadingTemplates}
                  />
                  <span className={selectedTemplate === '' ? 'font-bold' : ''}>
                    Automatisch auswählen (AI entscheidet)
                  </span>
                </label>
                {templates.map((template) => (
                  <label
                    key={template.id}
                    className="form-check"
                    style={{
                      alignItems: 'flex-start',
                      padding: 'var(--space-2)',
                      borderRadius: 'var(--radius-sm)',
                      border: selectedTemplate === template.id ? '2px solid var(--color-primary)' : '1px solid var(--color-border)',
                      backgroundColor: selectedTemplate === template.id ? 'var(--color-primary-50)' : 'var(--color-surface)'
                    }}
                  >
                    <input
                      type="radio"
                      name="template"
                      value={template.id}
                      checked={selectedTemplate === template.id}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      disabled={loading || loadingTemplates}
                      style={{ marginTop: '3px' }}
                    />
                    {template.imageUrl && (
                      <img
                        src={template.imageUrl}
                        alt={template.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: 'var(--radius-sm)',
                          flexShrink: 0
                        }}
                      />
                    )}
                    <div className="flex-1">
                      <div className={selectedTemplate === template.id ? 'font-bold' : ''}>
                        {template.name}
                      </div>
                      <div className="text-xs text-muted mt-1">
                        {template.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {loadingTemplates && (
                <p className="text-xs text-muted mt-1">Lade Templates...</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !topic.trim()}
              className="btn btn-primary btn-full btn-lg"
            >
              {loading ? 'Generiere Meme...' : 'Meme Generieren'}
            </button>
          </form>

          {error && (
            <div className="alert alert-error mt-4">{error}</div>
          )}
        </div>

        {/* Right Column - Templates Preview */}
        <div className="card">
          <h2 className="card-title mb-4">Template Vorschau</h2>

          {loadingTemplates ? (
            <p>Lade Templates...</p>
          ) : templates.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {selectedTemplate ? (
                (() => {
                  const template = templates.find(t => t.id === selectedTemplate);
                  if (!template) return null;
                  return (
                    <div>
                      <h3 className="info-panel-title" style={{ color: 'var(--color-primary)' }}>
                        Ausgewähltes Template
                      </h3>
                      <div style={{
                        border: '3px solid var(--color-primary)',
                        borderRadius: 'var(--radius-lg)',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--color-bg-secondary)',
                        marginBottom: 'var(--space-5)'
                      }}>
                        <div className="font-bold mb-2" style={{ fontSize: 'var(--text-lg)' }}>
                          {template.name}
                        </div>
                        <div style={{
                          backgroundColor: 'var(--color-surface)',
                          border: '2px dashed var(--color-primary)',
                          borderRadius: 'var(--radius-md)',
                          textAlign: 'center',
                          minHeight: '200px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          marginBottom: 'var(--space-3)',
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {template.imageUrl ? (
                            <img
                              src={template.imageUrl}
                              alt={template.name}
                              style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: 'var(--radius-sm)'
                              }}
                            />
                          ) : (
                            <>
                              <div className="font-bold" style={{
                                padding: 'var(--space-3)',
                                backgroundColor: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-sm)'
                              }}>
                                [TOP TEXT]
                              </div>
                              <div className="text-sm text-muted" style={{
                                fontStyle: 'italic',
                                padding: 'var(--space-5)'
                              }}>
                                {template.name}
                              </div>
                              <div className="font-bold" style={{
                                padding: 'var(--space-3)',
                                backgroundColor: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-sm)'
                              }}>
                                [BOTTOM TEXT]
                              </div>
                            </>
                          )}
                        </div>
                        <div className="text-sm text-secondary mt-2">
                          <strong>Kategorie:</strong> {template.category}
                        </div>
                        <div className="text-sm text-secondary mt-1">
                          {template.description}
                        </div>
                      </div>
                      <div className="text-xs text-muted mt-4">
                        Tipp: Wähle ein anderes Template aus der Liste links
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div>
                  <p className="text-sm text-secondary mb-4">
                    Wähle ein Template aus oder lasse die AI automatisch wählen
                  </p>
                  <div className="grid-2-col">
                    {templates.slice(0, 8).map((template) => (
                      <div
                        key={template.id}
                        className="card card-interactive"
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        <div style={{
                          backgroundColor: 'var(--color-surface)',
                          border: '1px dashed var(--color-border)',
                          borderRadius: 'var(--radius-sm)',
                          overflow: 'hidden',
                          textAlign: 'center',
                          minHeight: '100px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          marginBottom: 'var(--space-2)',
                          fontSize: 'var(--text-xs)'
                        }}>
                          {template.imageUrl ? (
                            <img
                              src={template.imageUrl}
                              alt={template.name}
                              style={{
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain'
                              }}
                            />
                          ) : (
                            <div style={{ padding: '20px 10px' }}>
                              <div className="font-bold">[TOP]</div>
                              <div className="text-xs text-muted">&#x1f4f8;</div>
                              <div className="font-bold">[BOTTOM]</div>
                            </div>
                          )}
                        </div>
                        <div className="text-xs font-bold text-center">
                          {template.name}
                        </div>
                      </div>
                    ))}
                  </div>
                  {templates.length > 8 && (
                    <p className="text-xs text-muted mt-2 text-center">
                      +{templates.length - 8} weitere Templates verfügbar
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <p>Keine Templates gefunden.</p>
              <button onClick={loadTemplates} className="btn btn-primary">
                Templates laden
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Result Section */}
      {result && (
        <div className="result-container">
          <div className="info-panel info-panel-success">
            <div className="info-panel-title">Dein Meme wurde erstellt!</div>
            <div className="text-sm">
              Die Texte werden automatisch an den passenden Stellen des Templates platziert. Ziehe sie bei Bedarf an die gewünschte Position, passe Größe, Schriftart, Farbe und Umrandung an.
            </div>
          </div>

          <div className="grid-2-col mt-4">
            {/* Left: Interactive Meme Preview */}
            <div>
              <h3 className="font-semibold mb-4">Generiertes Meme</h3>
              <div style={{
                border: '3px solid var(--color-success)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-5)',
                backgroundColor: 'var(--color-bg-secondary)',
                boxShadow: 'var(--shadow-lg)'
              }}>
                {result.template.imageUrl ? (
                  <div
                    ref={previewContainerRef}
                    style={{
                      backgroundColor: 'var(--color-surface)',
                      borderRadius: 'var(--radius-md)',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: 'var(--shadow-sm)',
                      touchAction: 'none',
                    }}
                    onMouseMove={onMouseMove}
                    onMouseUp={onMouseUp}
                    onMouseLeave={onMouseUp}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchEnd}
                  >
                    <img
                      src={result.template.imageUrl}
                      alt={result.template.name}
                      style={{
                        width: '100%',
                        height: 'auto',
                        display: 'block',
                        pointerEvents: 'none',
                        userSelect: 'none',
                      }}
                      draggable={false}
                    />
                    {/* One draggable overlay per text box */}
                    {texts.map((text, i) => (
                      textPositions[i] ? (
                        <div
                          key={i}
                          style={getTextOverlayStyle(textPositions[i], draggingIndex === i)}
                          onMouseDown={(e) => onMouseDown(i, e)}
                          onTouchStart={(e) => onTouchStart(i, e)}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255,255,255,0.6)';
                          }}
                          onMouseLeave={(e) => {
                            if (draggingIndex !== i) {
                              (e.currentTarget as HTMLDivElement).style.borderColor = 'transparent';
                            }
                          }}
                        >
                          {text.toUpperCase() || `TEXT ${i + 1}`}
                        </div>
                      ) : null
                    ))}
                  </div>
                ) : (
                  /* No image URL - text only fallback */
                  <div style={{
                    padding: '30px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: '300px',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)'
                  }}>
                    <div style={{
                      textAlign: 'center',
                      marginBottom: 'var(--space-3)',
                    }}>
                      <div className="text-sm font-bold">{result.template.name}</div>
                      <div className="text-xs text-muted mt-1">
                        Template: {result.template.category}
                      </div>
                    </div>
                    {texts.map((text, i) => (
                      <div
                        key={i}
                        className="font-bold"
                        style={{
                          fontSize: 'var(--text-xl)',
                          padding: 'var(--space-4)',
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderRadius: 'var(--radius-md)',
                          border: '2px solid var(--color-border)',
                          textTransform: 'uppercase',
                          textAlign: 'center',
                          marginBottom: 'var(--space-3)',
                        }}
                      >
                        {text || `Text ${i + 1}`}
                      </div>
                    ))}
                  </div>
                )}

                {/* Download / Actions */}
                <div className="btn-group mt-4" style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  {result.template.imageUrl && (
                    <button
                      onClick={handleDownload}
                      className="btn btn-success"
                      style={{ flex: 1 }}
                    >
                      Bild herunterladen
                    </button>
                  )}
                  <button
                    onClick={handleReset}
                    className="btn btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Neues Meme erstellen
                  </button>
                </div>
              </div>
            </div>

            {/* Right: Meme Details & Edit */}
            <div>
              <h3 className="font-semibold mb-4">Texte anpassen</h3>

              <div className="card mb-4">
                {/* One control group per text box */}
                {texts.map((text, i) => (
                  <div className="form-group" key={i}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <strong>Textfeld {i + 1}:</strong>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => adjustFontSize(i, -FONT_SIZE_STEP)}
                          disabled={!textPositions[i] || textPositions[i].fontSize <= MIN_FONT_SIZE}
                          className="btn btn-secondary"
                          style={{
                            padding: '2px 10px',
                            fontSize: '16px',
                            lineHeight: '1',
                            minWidth: '32px',
                          }}
                          title="Schrift verkleinern"
                        >
                          -
                        </button>
                        <span className="text-sm" style={{ minWidth: '40px', textAlign: 'center' }}>
                          {textPositions[i]?.fontSize ?? DEFAULT_FONT_SIZE}px
                        </span>
                        <button
                          type="button"
                          onClick={() => adjustFontSize(i, FONT_SIZE_STEP)}
                          disabled={!textPositions[i] || textPositions[i].fontSize >= MAX_FONT_SIZE}
                          className="btn btn-secondary"
                          style={{
                            padding: '2px 10px',
                            fontSize: '16px',
                            lineHeight: '1',
                            minWidth: '32px',
                          }}
                          title="Schrift vergrößern"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={text}
                      onChange={(e) => updateText(i, e.target.value)}
                      className="form-textarea mt-1"
                      placeholder={`Text für Feld ${i + 1}...`}
                    />
                  </div>
                ))}

                {/* Font & colour styling */}
                <div className="form-group">
                  <label className="form-label">Schriftart</label>
                  <select
                    value={fontFamily}
                    onChange={(e) => setFontFamily(e.target.value)}
                    className="form-select"
                  >
                    {FONT_OPTIONS.map((font) => (
                      <option key={font.label} value={font.value}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Schriftfarbe</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="color"
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                        style={{ width: '40px', height: '32px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                        title="Schriftfarbe wählen"
                      />
                      <span className="text-xs text-muted">{textColor.toUpperCase()}</span>
                    </div>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label className="form-label">Umrandung</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="color"
                        value={outlineColor}
                        onChange={(e) => setOutlineColor(e.target.value)}
                        style={{ width: '40px', height: '32px', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
                        title="Umrandungsfarbe wählen"
                      />
                      <span className="text-xs text-muted">{outlineColor.toUpperCase()}</span>
                    </div>
                  </div>
                </div>

                {/* Quick colour presets for readable contrast */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
                  <button
                    type="button"
                    onClick={() => { setTextColor('#FFFFFF'); setOutlineColor('#000000'); }}
                    className="btn btn-secondary"
                    style={{ fontSize: 'var(--text-xs)', padding: '4px 10px' }}
                  >
                    Weiß auf Schwarz
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTextColor('#000000'); setOutlineColor('#FFFFFF'); }}
                    className="btn btn-secondary"
                    style={{ fontSize: 'var(--text-xs)', padding: '4px 10px' }}
                  >
                    Schwarz auf Weiß
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTextColor('#FFE600'); setOutlineColor('#000000'); }}
                    className="btn btn-secondary"
                    style={{ fontSize: 'var(--text-xs)', padding: '4px 10px' }}
                  >
                    Gelb auf Schwarz
                  </button>
                </div>

                {/* Reset positions button */}
                <button
                  onClick={() => {
                    if (result?.template) {
                      initializeTextPositions(result.template, texts.length);
                    }
                  }}
                  className="btn btn-secondary btn-full mt-2"
                  style={{ fontSize: 'var(--text-sm)' }}
                >
                  Positionen zurücksetzen
                </button>
              </div>

              <div className="info-panel" style={{
                backgroundColor: 'var(--color-info-light)',
                borderLeft: '4px solid var(--color-info)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-4)',
              }}>
                <div className="text-sm" style={{ margin: 0 }}>
                  <strong>Tipp:</strong> Klicke und ziehe die Texte auf dem Bild, um sie zu verschieben. Nutze die +/- Buttons für die Schriftgröße sowie Schriftart-, Farb- und Umrandungs-Auswahl für den optimalen Look. Die Vorschau entspricht dem Download.
                </div>
              </div>

              <div className="info-panel info-panel-warning">
                <h4 className="info-panel-title">Warum funktioniert das?</h4>
                <p className="text-sm" style={{ margin: 0 }}>{result.explanation}</p>
              </div>

              <div className="info-panel info-panel-success">
                <h4 className="info-panel-title">Template Info</h4>
                <p className="text-sm mb-2">
                  <strong>Name:</strong> {result.template.name}
                </p>
                <p className="text-sm mb-2">
                  <strong>Kategorie:</strong> {result.template.category}
                </p>
                <p className="text-xs text-secondary" style={{ margin: 0 }}>
                  {result.template.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemeGeneratorPage;
